import { useEffect, useState } from 'react';
import { getCategoryColors, DEFAULT_CATEGORY_COLOR } from '@/lib/category-colors';
import { toCents, fromCents, formatMoneyBRL } from '@/lib/utils';

interface TotalsSummaryProps {
  className?: string;
}

type StyleCategory = 'health' | 'education' | 'infrastructure' | 'diverse';

export function TotalsSummary({ className }: TotalsSummaryProps) {
  const [items, setItems] = useState<Array<{ title: string; total: number; style: StyleCategory; categoryKey?: string }>>([]);
  const [general, setGeneral] = useState(0);

  const formatCurrency = (value: number) => formatMoneyBRL(value);

  // As cores serão resolvidas dinamicamente via getCategoryColors a partir de data-category-key

  useEffect(() => {
    const calculateDynamicTotals = () => {
      const sections = document.querySelectorAll<HTMLElement>(
        'section.secao-health, section.secao-education, section.secao-infrastructure, section.secao-diverse'
      );

      const map = new Map<string, { total: number; style: StyleCategory; categoryKey?: string }>();

      sections.forEach((section) => {
        const title = (section.querySelector('h3')?.textContent || '').trim();
        const styleClass = Array.from(section.classList).find((cls) => cls.startsWith('secao-')) || 'secao-diverse';
        const style = (styleClass.replace('secao-', '') as StyleCategory) || 'diverse';
        const categoryKey = (section.getAttribute('data-category-key') || '').trim() || undefined;

        const values = section.querySelectorAll<HTMLElement>('[data-value]');
        const totalCents = Array.from(values).reduce((sum, el) => {
          const raw = el.getAttribute('data-value') || '0';
          const value = Number(raw);
          const cents = Number.isFinite(value) ? toCents(value) : 0;
          return sum + cents;
        }, 0);
        const total = fromCents(totalCents);

        if (title) {
          const prev = map.get(title);
          map.set(title, { total: (prev?.total || 0) + total, style, categoryKey });
        }
      });

      const arr = Array.from(map.entries()).map(([title, { total, style, categoryKey }]) => ({ title, total, style, categoryKey }));

      arr.sort((a, b) => {
        const prio = (s: StyleCategory) => (s === 'health' ? 0 : s === 'education' ? 1 : s === 'infrastructure' ? 2 : 3);
        const dp = prio(a.style) - prio(b.style);
        if (dp !== 0) return dp;
        return a.title.localeCompare(b.title, 'pt-BR');
      });

      setItems(arr);
      // Soma em centavos para evitar erro de ponto flutuante
      const generalCents = arr.reduce((sum, i) => sum + toCents(i.total || 0), 0);
      setGeneral(fromCents(generalCents));
    };

    // cálculo inicial e atualização periódica
    calculateDynamicTotals();
    const interval = setInterval(calculateDynamicTotals, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className={`mb-8 ${className || ''}`}>
      <div className="bg-card rounded-xl shadow-elevated p-6">
        <h3 className="text-2xl font-bold text-foreground mb-6 font-inter">
          <i className="fas fa-calculator text-primary mr-3"></i>
          Resumo por categoria
        </h3>

        <div className="space-y-4">
          {items.map((item) => {
            const colors = item.categoryKey ? getCategoryColors(item.categoryKey) : DEFAULT_CATEGORY_COLOR;
            return (
              <div key={item.title} className={`flex justify-between items-center p-4 ${colors.bgClass} rounded-lg ${colors.borderClass ? `border ${colors.borderClass}` : ''}`}>
                <span className={`font-medium ${colors.textClass}`}>{item.title}:</span>
                <span className={`font-bold ${colors.textClass} text-xl`}>
                  {formatCurrency(item.total)}
                </span>
              </div>
            );
          })}

          <div className={`flex justify-between items-center p-4 ${DEFAULT_CATEGORY_COLOR.bgClass} rounded-lg border-2 ${DEFAULT_CATEGORY_COLOR.borderClass}`}>
            <span className={`font-bold ${DEFAULT_CATEGORY_COLOR.textClass} text-lg`}>Total Geral:</span>
            <span className={`font-bold ${DEFAULT_CATEGORY_COLOR.textClass} text-2xl`}>
              {formatCurrency(general)}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}